import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Check, ChevronDown, ChevronUp, ClipboardList, Copy, ExternalLink, Eye, HardDrive, LayoutGrid, Loader2, Send, Undo2, Youtube } from "lucide-react";
import { Markdown } from "./Markdown";
import { openFilePreview, driveIdFromLink } from "./FilePreviewDrawer";
import { api } from "../lib/api";
import { fileUrl } from "./MessageExtras";
import { PlanResultsStrip } from "./PlanResultsStrip";

// Textarea grows with its content (no hidden text), capped and scrollable when very long.
const autoGrow = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 220) + "px"; };

// Collapsible activity timeline stored on an assistant message (Berpikir… → Mendelegasikan ke Agent X → selesai).
export function ActivityTimeline({ items = [], live = false }) {
  const [open, setOpen] = useState(live);
  if (!items.length) return null;
  const fmt = (ms) => (ms == null ? "" : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} dtk`);
  return (
    <div className="mt-2 text-xs" data-testid="activity-timeline">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-slate-400 hover:text-slate-600" data-testid="activity-toggle">
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Aktivitas asisten ({items.length})
      </button>
      {open && <ol className="mt-1.5 space-y-1 border-l border-slate-200 pl-3">
        {items.map((a, i) => (
          <li key={i} className="flex items-center gap-2 text-slate-500" data-testid={`activity-item-${i}`}>
            {a.done ? <Check size={11} className="text-emerald-500" /> : <Loader2 size={11} className="animate-spin text-[#2F6BFF]" />}
            <span>{a.label}</span>{a.ms != null && <span className="text-slate-400">· {fmt(a.ms)}</span>}
          </li>
        ))}
      </ol>}
    </div>
  );
}

// Card asking the user to connect Google Drive / Calendar / Gmail (tool: drive_connect | workspace_connect) — links to Profil › Integrasi.
export function DriveConnectCard({ m }) {
  if (m.tool !== "drive_connect" && m.tool !== "workspace_connect") return null;
  const label = m.tool === "workspace_connect" ? (m.product === "gmail" ? "Hubungkan Gmail" : "Hubungkan Google Calendar") : "Hubungkan Google Drive";
  return (
    <a href="/integrations" className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="drive-connect-card">
      <HardDrive size={14} /> {label} → Integrasi
    </a>
  );
}

// Saved/read Drive file (tool: drive_save | drive_read) — opens the in-app preview drawer; the Drive link stays as a secondary action.
export function DriveFileCard({ m }) {
  if (!["drive_save", "drive_read"].includes(m.tool) || !m.drive) return null;
  const link = m.drive.link || m.drive.webViewLink;
  const id = m.drive.id || driveIdFromLink(link || "");
  return (
    <span className="mt-2 flex items-center overflow-hidden rounded-xl border border-slate-200 bg-white text-xs text-slate-700 hover:border-emerald-400" data-testid="drive-file-card">
      <button type="button" onClick={() => (id ? openFilePreview({ name: m.drive.name, driveId: id, link }) : window.open(link, "_blank"))} className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left" title="Lihat pratinjau" data-testid="drive-file-preview">
        <HardDrive size={14} className="shrink-0 text-emerald-600" /><span className="truncate font-semibold">{m.drive.name}</span><span className="shrink-0 text-slate-400">· Google Drive › Oryntix</span><Eye size={11} className="ml-auto shrink-0 text-slate-400" />
      </button>
      {link && <a href={link} target="_blank" rel="noreferrer" className="border-l border-slate-200 px-2.5 py-2 text-slate-400 hover:text-emerald-600" title="Buka di Google Drive" data-testid="drive-file-open"><ExternalLink size={11} /></a>}
    </span>
  );
}

// "Pilih file dari Drive" (tool: drive_pick) — opens the Google Picker; the chosen documents are sent with a read request.
export function DrivePickCard({ m, isLast, act }) {
  if (m.tool !== "drive_pick" || !isLast) return null;
  return (
    <button type="button" onClick={() => act.pickDrive?.(m.pick_text || "Baca dokumen ini dan rangkum isinya.")} className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="drive-pick-card">
      <HardDrive size={14} /> Pilih file dari Google Drive
    </button>
  );
}

export function YouTubeCard({ m }) {
  if (m.tool !== "youtube" || !m.youtube_id) return null;
  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-black" data-testid="youtube-card">
      <iframe title="YouTube" src={`https://www.youtube.com/embed/${m.youtube_id}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="aspect-video w-full" />
      <a href={m.youtube_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 bg-white px-3 py-1.5 text-xs text-slate-500 hover:text-slate-800"><Youtube size={12} /> Buka di YouTube</a>
    </div>
  );
}

export function UrlImages({ m }) {
  if (m.tool !== "image_results" || !(m.url_images || []).length) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="url-images">
      {m.url_images.map((im, i) => <a key={i} href={im.url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg"><img src={im.url} alt={im.name} loading="lazy" className="h-32 w-full object-cover" /></a>)}
    </div>
  );
}

// Chip "Memori disimpan/diperbarui" → click to see & undo.
export function MemoryChip({ m }) {
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);
  if (m.tool !== "memory_saved" || !m.memory || gone) return null;
  const undo = async () => {
    try {
      if (m.memory_action === "updated" && m.memory.previous_text) await api.put(`/memory/${m.memory.id}`, { text: m.memory.previous_text });
      else await api.delete(`/memory/${m.memory.id}`);
      setGone(true); toast.success("Memori dibatalkan");
    } catch { toast.error("Gagal membatalkan memori"); }
  };
  return (
    <div className="mt-2" data-testid="memory-chip">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 rounded-full bg-[#F3EEFF] px-2.5 py-1 text-[11px] font-semibold text-[#7C3AED]" data-testid="memory-chip-toggle">
        <Brain size={11} /> Memori {m.memory_action === "updated" ? "diperbarui" : "disimpan"}
      </button>
      {open && <div className="mt-1.5 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
        <span className="flex-1" data-testid="memory-chip-text">{m.memory.text}</span>
        <button type="button" onClick={undo} className="flex items-center gap-1 text-slate-500 hover:text-red-600" data-testid="memory-chip-undo"><Undo2 size={11} /> Batalkan</button>
      </div>}
    </div>
  );
}

// Questionnaire card: 1–5 questions with chip options (single/multi) + optional free text; answers are sent as one user message.
export function QuestionnaireCard({ m, isLast, onSend }) {
  const [ans, setAns] = useState({});
  const [other, setOther] = useState({});
  const [sent, setSent] = useState(false);
  const qs = m.questionnaire?.questions || [];
  if (m.tool !== "questionnaire" || !qs.length) return null;
  const pick = (q, o) => setAns((a) => { const cur = a[q.id] || []; return { ...a, [q.id]: q.multi ? (cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]) : [o] }; });
  const answered = qs.filter((q) => (ans[q.id] || []).length || (other[q.id] || "").trim()).length;
  const submit = () => {
    const lines = qs.map((q, i) => { const parts = [...(ans[q.id] || []), ...((other[q.id] || "").trim() ? [other[q.id].trim()] : [])]; return `${i + 1}. ${q.q}: ${parts.length ? parts.join(", ") : "(dilewati)"}`; });
    setSent(true); onSend(lines.join("\n"));
  };
  const done = sent || !isLast;
  return (
    <div className="mt-2 space-y-3 rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] p-3" data-testid="questionnaire-card">
      {qs.map((q, qi) => (
        <div key={q.id} data-testid={`questionnaire-q-${qi}`}>
          <p className="text-sm font-semibold text-slate-800">{qi + 1}. {q.q}{q.multi && <span className="ml-1 text-[11px] font-normal text-slate-400">(boleh lebih dari satu)</span>}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {q.options.map((o, oi) => { const on = (ans[q.id] || []).includes(o); return (
              <button key={oi} type="button" disabled={done} onClick={() => pick(q, o)} data-testid={`questionnaire-opt-${qi}-${oi}`} aria-pressed={on}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${on ? "border-[#2F6BFF] bg-[#2F6BFF] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#2F6BFF]"}`}>{o}</button>
            ); })}
          </div>
          {q.other && !done && <input value={other[q.id] || ""} onChange={(e) => setOther((x) => ({ ...x, [q.id]: e.target.value }))} placeholder="Lainnya…" className="input-dark mt-1.5 w-full py-1 text-xs" data-testid={`questionnaire-other-${qi}`} />}
        </div>
      ))}
      {!done && <button type="button" onClick={submit} disabled={!answered} className="btn-grad flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs disabled:opacity-50" data-testid="questionnaire-submit"><Send size={12} /> Kirim jawaban ({answered}/{qs.length})</button>}
      {sent && <p className="text-xs text-slate-400">Jawaban terkirim.</p>}
    </div>
  );
}

// Markdown deliverable with a Copy button (tool show_markdown).
export function MarkdownCard({ m }) {
  const [copied, setCopied] = useState(false);
  const card = m.markdown_card;
  if (!card?.content) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(card.content); } catch { const ta = document.createElement("textarea"); ta.value = card.content; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-[#D9E2F2] bg-white text-slate-800" data-testid="markdown-card">
      <div className="flex items-center justify-between gap-2 border-b border-[#E7ECF3] bg-[#F5F8FF] px-3 py-2">
        <p className="truncate text-xs font-semibold text-slate-700" data-no-i18n>{card.title}</p>
        <button type="button" onClick={copy} className="flex shrink-0 items-center gap-1 rounded-lg bg-white px-2 py-1 text-[11px] font-semibold text-[#2F6BFF] shadow-sm hover:bg-[#EEF3FF]" data-testid="markdown-card-copy">
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Tersalin" : "Salin"}
        </button>
      </div>
      <div className="max-h-[420px] overflow-y-auto px-3 py-2 text-sm" data-no-i18n><Markdown content={card.content} /></div>
    </div>
  );
}

// Ringkasan Kebutuhan (skill tool show_requirement_summary): the brief as the user said it, editable inline on the latest card.
// Lanjutkan sends "Lanjutkan" when untouched, else the corrected rows (the runtime still renders right away); Perbaiki opens a free-text correction.
const BRIEF_ROWS = [["goal", "Tujuan"], ["context", "Konteks"], ["constraints", "Batasan"], ["deliverable", "Hasil"]];
export function RequirementSummaryCard({ m, isLast, onSend }) {
  const r = m.requirement_summary;
  const [rows, setRows] = useState(() => Object.fromEntries(BRIEF_ROWS.map(([k]) => [k, r?.[k] || ""])));
  const [picked, setPicked] = useState(m.choice_picked || null);
  useEffect(() => { if (r) setRows(Object.fromEntries(BRIEF_ROWS.map(([k]) => [k, r[k] || ""]))); }, [m.brief_revised_at]); // eslint-disable-line react-hooks/exhaustive-deps
  if (m.tool !== "requirement_summary" || !r) return null;
  const old = !!m.superseded_by; // re-sent / corrected below: dimmed, buttons off
  const live = !old && !m.summary_closed && (isLast || !!m.intake_live) && !picked; // the newest open card stays live while prose turns flow below it
  const edited = BRIEF_ROWS.some(([k]) => (rows[k] || "").trim() !== (r[k] || "").trim());
  const go = () => {
    setPicked("go");
    onSend(edited ? "Ringkasan diperbaiki — " + BRIEF_ROWS.filter(([k]) => rows[k]?.trim()).map(([k, label]) => `${label}: ${rows[k].trim()}`).join(". ") + ". Lanjutkan dengan ringkasan ini." : "Lanjutkan", { brief_confirm: true });
  };
  return (
    <div id={`req-card-${m.id}`} className={`mt-2 w-full max-w-xl rounded-2xl border border-[#2F6BFF]/25 bg-[#F5F8FF] p-3 transition-opacity ${old ? "opacity-50" : ""}`} data-testid={old ? "requirement-summary-card-old" : "requirement-summary-card"}>
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#2F6BFF]">
        Ringkasan Kebutuhan · <span data-no-i18n>{r.title}</span>
        {m.resent_from && <span className="ml-2 whitespace-nowrap rounded-full bg-[#2F6BFF]/10 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal" data-testid="requirement-summary-resent">dikirim ulang</span>}
        {m.summary_closed && !old && <span className="ml-2 whitespace-nowrap rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-emerald-600" data-testid="requirement-summary-status">{m.summary_paused ? "dijeda" : m.summary_ended ? "ditutup" : "disetujui"}</span>}
        {live && <span className="ml-2 font-medium normal-case tracking-normal text-slate-400">klik baris untuk mengubah</span>}
      </p>
      <dl className="space-y-1.5">
        {BRIEF_ROWS.filter(([k]) => r[k] || live).map(([k, label]) => (
          <div key={k} className="grid grid-cols-[76px_1fr] gap-2 text-xs" data-testid={`requirement-summary-${k}`}>
            <dt className="pt-1 font-semibold text-slate-500">{label}</dt>
            {live ? (
              <textarea rows={1} value={rows[k]} ref={autoGrow} onChange={(e) => { setRows((x) => ({ ...x, [k]: e.target.value })); autoGrow(e.target); }} data-no-i18n data-testid={`requirement-summary-edit-${k}`}
                className="min-h-[28px] max-h-[220px] w-full resize-none overflow-y-auto rounded-lg border border-transparent bg-white/70 px-2 py-1 text-slate-800 outline-none transition focus:border-[#2F6BFF]/50 focus:bg-white" />
            ) : (
              <dd className="pt-1 text-slate-800" data-no-i18n>{rows[k] || r[k]}</dd>
            )}
          </div>
        ))}
      </dl>
      {old && (
        <button type="button" onClick={() => document.getElementById(`req-card-${m.superseded_by}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} data-testid="requirement-summary-jump-latest" className="mt-2 text-[11px] font-semibold text-[#2F6BFF] underline-offset-2 hover:underline">
          Lihat versi terbaru di bawah
        </button>
      )}
      {live && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="requirement-summary-actions">
          <button type="button" onClick={go} data-testid="requirement-summary-go" className="btn-grad rounded-xl px-3.5 py-2 text-xs font-semibold">{edited ? "Simpan & Lanjutkan" : "Lanjutkan"}</button>
          <button type="button" onClick={() => { setPicked("fix"); onSend("Ada yang perlu diperbaiki di ringkasannya:"); }} data-testid="requirement-summary-fix" className="rounded-xl border border-[#2F6BFF]/40 bg-white px-3.5 py-2 text-xs font-semibold text-[#2F6BFF] hover:bg-[#EEF3FF]">Perbaiki</button>
        </div>
      )}
    </div>
  );
}

// One or more buttons; the clicked value is sent as the user's message (tool choice_buttons). Only the latest card is clickable.
export function ChoiceButtons({ m, isLast, onSend }) {
  const [picked, setPicked] = useState(m.choice_picked || null);
  const btns = m.choice_buttons?.buttons || [];
  if (!btns.length) return null;
  const done = !!picked || !isLast;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="choice-buttons">
      {btns.map((b) => (
        <button key={b.id} type="button" disabled={done} onClick={() => { setPicked(b.id); onSend(b.value); }} data-testid={`choice-btn-${b.id}`} aria-pressed={picked === b.id}
          className={`rounded-xl px-3.5 py-2 text-xs font-semibold transition disabled:cursor-default ${picked === b.id ? "btn-grad" : done ? "border border-[#E7ECF3] text-slate-400" : "border border-[#2F6BFF]/40 bg-white text-[#2F6BFF] hover:bg-[#EEF3FF]"}`}>
          {b.label}
        </button>
      ))}
    </div>
  );
}

// Link to the workspace the assistant just acted on (tools workspace_*).
// Governance cards: task links from a general chat (content stays in the task room), room summary, state-tool chips.
export function WorkspaceLinkCard({ m, act, cid }) {
  const items = m.workspace_links;
  if (m.tool !== "workspace_link" || !Array.isArray(items)) return null;
  const open = async (t) => {
    try {
      const r = await api.post(`/tasks/${t.id}/discuss`, { mode: "chat", from_conversation_id: cid });
      act.nav(`/chat/${r.data.conversation_id}`);
    } catch (e) { act.nav(`/workspace/${t.id}`); }
  };
  const color = (s) => ({ completed: "bg-emerald-100 text-emerald-700", running: "bg-sky-100 text-sky-700", paused: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700" }[s] || "bg-slate-100 text-slate-600");
  return (
    <div className="mt-2 w-full max-w-xl space-y-1.5" data-testid="workspace-link-card">
      {items.length === 0 && <p className="text-xs text-slate-500" data-testid="workspace-link-empty">Tidak ada tugas Ruang Kerja yang cocok.</p>}
      {items.map((t) => (
        <div key={t.id} className="flex items-center gap-2 rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] px-3 py-2 text-xs" data-testid={`workspace-link-${t.id}`}>
          <LayoutGrid size={14} className="shrink-0 text-[#2F6BFF]" />
          <span className="min-w-0 flex-1"><span className="block truncate font-semibold text-slate-800" data-no-i18n>{t.title}</span><span className={`mt-0.5 inline-block rounded-full px-1.5 py-px text-[10px] font-semibold ${color(t.status)}`}>{t.status || "—"}</span></span>
          <button type="button" onClick={() => open(t)} className="btn-grad shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold" data-testid={`open-task-room-${t.id}`}>Buka ruang tugas</button>
        </div>
      ))}
    </div>
  );
}

export function ConversationSummaryCard({ m }) {
  const s = m.conversation_summary;
  if (m.tool !== "conversation_summary" || !s) return null;
  const Sec = ({ title, items, tid }) => (
    <div data-testid={tid}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>{(items || []).length ? <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-xs text-slate-700">{items.map((x, i) => <li key={i} data-no-i18n>{x}</li>)}</ul> : <p className="text-xs text-slate-400">—</p>}</div>
  );
  return (
    <div className="mt-2 w-full max-w-xl space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="conversation-summary-card">
      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Ringkasan percakapan · tersimpan di memori room</p>
      <Sec title="Keputusan" items={s.decisions} tid="summary-decisions" /><Sec title="Hasil" items={s.results} tid="summary-results" /><Sec title="Masih terbuka" items={s.open} tid="summary-open" />
    </div>
  );
}

// Ruang Kerja event the assistant is replying to (stage completed / waiting approval / task completed / failed).
const EVENT_TONE = { stage_completed: "bg-sky-50 text-sky-700 border-sky-200", stage_waiting_approval: "bg-amber-50 text-amber-800 border-amber-200", task_completed: "bg-emerald-50 text-emerald-700 border-emerald-200", task_failed: "bg-rose-50 text-rose-700 border-rose-200" };
export function TaskEventChip({ m, act }) {
  const ev = m.task_event;
  if (!ev) return null;
  return (
    <button type="button" onClick={() => act.nav(`/workspace/${ev.task_id}`)} className={`mb-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${EVENT_TONE[ev.kind] || "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`task-event-${ev.kind}`}>
      <LayoutGrid size={12} className="shrink-0" /><span className="truncate" data-no-i18n>Ruang Kerja · {ev.label}</span>
    </button>
  );
}

const STATE_LABEL = { set_phase: "Fase", end_task: "Sesi ditutup", unload_skill: "Skill dilepas", submit_card: "Kartu disetujui" };
const PHASE_LABEL = { intake: "penggalian", composition: "ke Ruang Kerja", planning: "ke Ruang Kerja", workflow: "ke Ruang Kerja", execution: "pengerjaan", revision: "revisi" };
export function StateEventChips({ m }) {
  const ev = Array.isArray(m.state_events) ? m.state_events.filter((e) => e.ok !== false) : [];
  if (!ev.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1" data-testid="state-event-chips">
      {ev.map((e, i) => <span key={i} className="rounded-full border border-slate-200 bg-white px-2 py-px text-[10px] text-slate-500" title={e.result} data-testid={`state-chip-${e.tool}`}>{STATE_LABEL[e.tool] || e.tool}{e.tool === "set_phase" && e.args?.phase ? ` → ${PHASE_LABEL[e.args.phase] || e.args.phase}` : ""}</span>)}
    </div>
  );
}

export function WorkspaceRef({ m, act }) {
  const r = m.workspace_ref;
  if (!r?.task_id) return null;
  return (
    <button type="button" onClick={() => act.nav(`/workspace/${r.task_id}`)} className="mt-2 flex items-center gap-2 rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] px-3 py-2 text-left text-xs text-slate-700 hover:bg-[#EEF3FF]" data-testid="workspace-ref">
      <LayoutGrid size={14} className="shrink-0 text-[#2F6BFF]" />
      <span className="min-w-0"><span className="block truncate font-semibold" data-no-i18n>{r.title}</span><span className="text-[11px] text-slate-500">Buka di Ruang Kerja{typeof m.stage_index === "number" ? ` · tahap ${m.stage_index + 1}` : ""}</span></span>
    </button>
  );
}

// Meeting summary (/summary): the Moderator's notes are also saved as a «Notulen rapat» task — open it in Ruang Kerja.
export function MeetingNotesCard({ m, act }) {
  if (m.tool !== "meeting_notes" || !m.task_id) return null;
  return (
    <button type="button" onClick={() => act.nav(`/workspace/${m.task_id}`)} className="mt-2 flex w-full items-center gap-2 rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] px-3 py-2 text-left text-xs text-slate-700 transition hover:bg-[#EEF3FF]" data-testid="meeting-notes-card">
      <ClipboardList size={14} className="shrink-0 text-[#2F6BFF]" />
      <span className="min-w-0"><span className="block truncate font-semibold" data-no-i18n>{m.task_title || "Notulen rapat"}</span><span className="text-[11px] text-slate-500">Tersimpan di Ruang Kerja · klik untuk membuka</span></span>
    </button>
  );
}

// ---- Composition: the living Content Plan card (sections with status; the one being discussed is highlighted; inline edit sends a correction) ----
export function ContentPlanCard({ m, isLast, onSend }) {
  const p = m.content_plan;
  const [edit, setEdit] = useState(null);
  const [val, setVal] = useState("");
  if (m.tool !== "content_plan" || !p) return null;
  const secs = p.sections || [];
  const done = secs.filter((s) => s.status === "approved").length;
  const STATUS = { approved: ["bg-emerald-100 text-emerald-700", "siap"], running: ["bg-amber-100 text-amber-700", "dibahas"], review: ["bg-sky-100 text-sky-700", "cek ulang"], pending: ["bg-slate-100 text-slate-500", "belum"] };
  const save = (n) => { setEdit(null); if (val.trim()) onSend(`Bagian ${n} diperbaiki: ${val.trim()}`); };
  return (
    <div className="mt-2 w-full max-w-xl rounded-2xl border border-[#2F6BFF]/25 bg-[#F5F8FF] p-3" data-testid="content-plan-card">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#2F6BFF]">Rencana Konten · <span data-no-i18n>{p.title}</span> <span className="font-medium normal-case tracking-normal text-slate-400">{done}/{secs.length} siap</span>{m.plan_reopened && <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-[#2F6BFF]" data-testid="content-plan-reopened-badge">dibuka dari obrolan lain</span>}</p>
      {p.premise && <p className="mb-1 text-xs text-slate-700" data-no-i18n>{p.premise}</p>}
      {p.style && <p className="mb-2 text-[11px] text-slate-500" data-no-i18n>Gaya: {p.style}</p>}
      {p.workflow_task_id && <a href={`/workspace/${p.workflow_task_id}`} className="mb-2 inline-block rounded-lg bg-white/80 px-2 py-1 text-[11px] font-semibold text-[#2F6BFF] hover:underline" data-testid="content-plan-workspace-link">Berjalan di Ruang Kerja →</a>}
      <ol className="space-y-1.5">
        {secs.map((s) => {
          const cur = p.current === s.n && s.status !== "approved" && !p.workflow_task_id;
          const [cls, lbl] = STATUS[cur ? "running" : s.status] || STATUS.pending;
          return (
            <li key={s.n} className={`rounded-xl border px-2.5 py-1.5 text-xs transition ${cur ? "border-amber-300 bg-amber-50/60" : "border-transparent bg-white/70"}`} data-testid={`content-plan-section-${s.n}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-700" data-no-i18n>#{s.n} {s.title}</span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{lbl}</span>
              </div>
              {edit === s.n && isLast ? (
                <div className="mt-1">
                  <textarea autoFocus value={val} onChange={(e) => { setVal(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} data-no-i18n data-testid={`content-plan-edit-${s.n}`}
                    className="w-full resize-none rounded-lg border border-[#2F6BFF]/40 bg-white px-2 py-1 text-slate-800 outline-none" rows={2} />
                  <div className="mt-1 flex gap-2">
                    <button type="button" onClick={() => save(s.n)} className="btn-grad rounded-lg px-2.5 py-1 text-[11px] font-semibold" data-testid={`content-plan-save-${s.n}`}>Simpan</button>
                    <button type="button" onClick={() => setEdit(null)} className="rounded-lg border px-2.5 py-1 text-[11px]">Batal</button>
                  </div>
                </div>
              ) : (
                s.summary && <p className={`mt-0.5 text-slate-600 ${isLast ? "cursor-text hover:text-slate-900" : ""}`} onClick={() => isLast && (setEdit(s.n), setVal(s.summary))} data-no-i18n>{s.summary}</p>
              )}
            </li>
          );
        })}
      </ol>
      <PlanResultsStrip plan={p} cid={m.conversation_id} />
    </div>
  );
}

// ---- Saved Content Plans from other chats: pick which one to reopen here ----
export function PlanPickerCard({ m }) {
  const items = m.plan_picker?.items || [];
  if (m.tool !== "plan_picker" || !items.length) return null;
  const KIND = { image: "Gambar", video: "Video", presentation: "Presentasi", document: "Dokumen" };
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="plan-picker-card">
      {items.map((p) => (
        <div key={p.k} className="w-[170px] rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[11px]" data-testid={`plan-picker-item-${p.k}`}>
          <p className="font-semibold text-slate-800"><span className="text-[#2F6BFF]">#{p.k}</span> <span data-no-i18n>{p.title}</span></p>
          <p className="mt-0.5 text-slate-500">{KIND[p.kind] || p.kind} · {p.approved}/{p.sections} bagian</p>
          {p.conversation_title && <p className="truncate text-slate-400" data-no-i18n>dari: {p.conversation_title}</p>}
        </div>
      ))}
    </div>
  );
}

// ---- Cross-result revision: pick which earlier result the revision refers to ----
export function ResultPickerCard({ m }) {
  const items = m.result_picker?.items || [];
  if (m.tool !== "result_picker" || !items.length) return null;
  const ICON = { document: "📄", presentation: "📊", video: "🎬", delegation: "🧠" };
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="result-picker-card">
      {items.map((r) => (
        <div key={r.n} className="w-[120px] overflow-hidden rounded-xl border border-slate-200 bg-white text-[11px]" data-testid={`result-picker-item-${r.n}`}>
          {r.thumb ? <img src={fileUrl(r.thumb)} alt="" className="h-[76px] w-full object-cover" /> : <div className="flex h-[76px] items-center justify-center bg-slate-50 text-2xl">{ICON[r.kind] || "🖼️"}</div>}
          <div className="px-2 py-1"><span className="font-semibold">#{r.n}</span> <span className="text-slate-600" data-no-i18n>{r.title}</span></div>
        </div>
      ))}
    </div>
  );
}

// ---- Package (composition render): progress, per-part failures with reason, retry only the failed parts ----
export function PackageCard({ m, cid }) {
  const [busy, setBusy] = useState(false);
  const pkg = m.package;
  const isVideo = pkg?.kind === "video";
  const rendering = isVideo && m.tool === "video" && m.rendering;
  if (!pkg || (m.tool !== "package" && m.tool !== "package_offer" && !rendering)) return null;
  const fails = pkg.failures || [];
  if (m.tool === "package" && !fails.length && !isVideo) return null; // image progress is shown by the task card; this card adds failure reasons + retry
  const partNo = (f) => (isVideo ? f.part : f.part + 1); // image packages index parts from 0, video scenes from 1
  const unit = isVideo ? "adegan" : "bagian";
  const retry = async () => {
    setBusy(true);
    try { await api.post(`/conversations/${cid}/messages/${m.id}/retry-failed`); toast.success("Bagian yang gagal diulang"); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengulang"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-3 text-xs" data-testid="package-card">
      <p className="font-semibold text-slate-700" data-no-i18n>{pkg.title || "Paket"} · {pkg.done ?? 0}/{pkg.total ?? (pkg.parts || []).length} {unit} jadi{pkg.merged ? " · versi gabungan tersedia" : ""}</p>
      {rendering && <p className="mt-1 text-[11px] text-slate-500" data-testid="package-rendering">{pkg.current ? `Adegan ${pkg.current} sedang dirender…` : "Menyiapkan render…"} Adegan yang sudah jadi muncul di bawah.</p>}
      {pkg.merge_failure && <p className="mt-1 text-[11px] text-amber-700" data-testid="package-merge-failure">Gabungan belum terbentuk: {pkg.merge_failure.message}. Klip per adegan tetap tersedia.</p>}
      {fails.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {fails.map((f) => (
            <li key={f.part} className="rounded-lg bg-rose-50 px-2 py-1 text-rose-700" data-testid={`package-failure-${partNo(f)}`}>
              <span className="font-semibold">{isVideo ? "Adegan" : "Bagian"} {partNo(f)}:</span> {f.message} — <span className="text-rose-600">{f.suggestion}</span>
            </li>
          ))}
        </ul>
      )}
      {fails.length > 0 && m.tool === "package" && (
        <button type="button" disabled={busy} onClick={retry} className="btn-grad mt-2 rounded-xl px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid="package-retry-failed">{busy ? "Mengulang…" : "Ulangi yang gagal"}</button>
      )}
    </div>
  );
}

// Hand-off card (set_phase composition in a general chat): the confirmed brief becomes a draft Ruang Kerja task with its own task room.
export function WorkspaceOfferCard({ m, act, cid }) {
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(m.accepted || null);
  useEffect(() => { if (m.accepted) setAccepted(m.accepted); }, [m.accepted]);
  const o = m.workspace_offer;
  if (m.tool !== "workspace_offer" || !o) return null;
  const go = async () => {
    if (accepted) return act.nav(`/chat/${accepted.room_id}`);
    setBusy(true);
    try {
      const r = await api.post(`/conversations/${cid}/messages/${m.id}/workspace-offer`);
      setAccepted(r.data);
      act.nav(`/chat/${r.data.room_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka room tugas"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 w-full max-w-xl rounded-2xl border border-[#2F6BFF]/25 bg-[#F5F8FF] p-3" data-testid="workspace-offer-card">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#2F6BFF]">Tugas Ruang Kerja · <span data-no-i18n>{o.title}</span></p>
      <p className="mb-2 text-xs text-slate-600">Tugas ini disusun bertahap, bukan langsung jadi. Deskripsinya diambil dari Ringkasan Kebutuhan; tahapannya kita susun bersama di room khusus tugas ini.</p>
      <dl className="space-y-1 text-xs">
        {BRIEF_ROWS.filter(([k]) => o.brief?.[k]).map(([k, label]) => (
          <div key={k} className="grid grid-cols-[76px_1fr] gap-2" data-testid={`workspace-offer-${k}`}><dt className="font-semibold text-slate-500">{label}</dt><dd className="text-slate-800" data-no-i18n>{o.brief[k]}</dd></div>
        ))}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={go} disabled={busy} data-testid="workspace-offer-go" className="btn-grad inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <LayoutGrid size={13} />}
          {accepted ? "Buka room tugas" : "Susun di Room Ruang Kerja"}
        </button>
        {accepted && <button type="button" onClick={() => act.nav(`/workspace/${accepted.task_id}`)} data-testid="workspace-offer-open-task" className="text-[11px] font-semibold text-[#2F6BFF] underline-offset-2 hover:underline">Lihat di Ruang Kerja</button>}
        {accepted && <span className="text-[11px] text-emerald-600" data-testid="workspace-offer-status">tugas dibuat</span>}
      </div>
    </div>
  );
}

